import { useState, useEffect, Fragment, ReactElement } from 'react';
import clsx from 'clsx';
import {
    Box, Button, TextField, InputAdornment, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TablePagination, Typography, Tooltip, Chip, Card, CardContent,
    FormControlLabel, Switch, Dialog, Snackbar, CircularProgress
} from '@material-ui/core';
import { Search, Email, WhatsApp } from '@material-ui/icons';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Loader } from '../../components/Loader/Loader';
import { ClassesType } from '../Classes.types';
import { DateFormats } from '../../helpers/Constants';
import { SetPageState, GetPageNyName } from '../../helpers/UI/SessionStorageManager';
import useRedirect from '../../helpers/Routes/Redirect';
import { sitePrefix } from '../../config';
import { getSmartSendList, deleteMapping } from '../../redux/reducers/smartSendSlice';
import { SmartSendListItem, eSendChannel } from '../../Models/DataSources/SmartSend';
import { eCampaignStatus } from '../../Models/Enums/Campaign';

// Own page-state key so this tab's page/search/filter never collide with the sources tab's
// 'DataSources' key (SessionStorageManager keys by PageName — different names never overwrite).
const PAGE_NAME_SS = 'DataSourcesSmartSend';
// Mirror of DataSources.tsx ROWS_OPTIONS (this is a separate module — same options, no shared const).
const ROWS_OPTIONS = [6, 10, 20, 50];

// Replicated from CampaignPicker.STATUS_KEY (that const is module-private) so the tab and the
// campaign picker can never disagree about what a campaign status is called. Same 7 statuses,
// same i18n keys (common.* / campaigns.*, which already exist and NewsletterManagment uses too).
// 12-fix-wave 7.7 — exported so the source-delete refusal (DataSources.tsx) names statuses the same way.
export const STATUS_KEY: { [k: number]: string } = {
    [eCampaignStatus.Created]: 'common.Created',
    [eCampaignStatus.Sending]: 'common.Sending',
    [eCampaignStatus.Stopped]: 'campaigns.Stopped',
    [eCampaignStatus.Finished]: 'common.Sent',
    [eCampaignStatus.Canceled]: 'campaigns.Canceled',
    [eCampaignStatus.OptinPending]: 'campaigns.Optin',
    [eCampaignStatus.ApprovePending]: 'campaigns.Approve',
};

// UI 2026-10-04 — a source name is usually ONE token with no space (`מבצע_קיץ_משתלם1_2026-08-23_12-52-24`),
// so it could not wrap and ran out of its cell. <wbr> after each `_` gives it a clean place to wrap (it is
// not copied with the text); the caller's overflowWrap 'anywhere' still breaks a name with no `_`.
// Used for the source and campaign columns here (a WhatsApp campaign name can hold the source name) and
// exported for DataSources.tsx's name column.
export const wrapAtUnderscores = (name: string | null | undefined) =>
    (name ?? '').split('_').map((part, i) => <Fragment key={i}>{i > 0 && <>_<wbr /></>}{part}</Fragment>);

// ══ 12-fix-wave 7.2 — ONE ROW PER SEND, DECIDED BY WHICH IDS ARE PRESENT (contract §1) ══════════
// `hasWa` also needs a status: a unit deleted in the WhatsApp product is ABSENT (§1.8).
const hasEmail = (row: SmartSendListItem): boolean => row.CampaignID != null;
const hasWa = (row: SmartSendListItem): boolean => row.WACampaignID != null && row.WACampaignStatus != null;
// §1.9 / D1 — every present channel still editable. A send that went out on any channel is closed.
const allPresentEditable = (row: SmartSendListItem): boolean =>
    (!hasEmail(row) || (row.CampaignStatus ?? 1) === 1)
    && (!hasWa(row) || row.WACampaignStatus === 1);
// FAIL CLOSED on a list row of the PREVIOUS API shape (no `WACampaignID` key at all, only `Channel`):
// there `CampaignID` can hold a WACampaignID, and opening or deleting it as an e-mail id would act on
// another id space. React ships with the API (contract §2); until then such a row offers no action.
const isOldShape = (row: SmartSendListItem): boolean => (row as any).WACampaignID === undefined;
// Open (Edit, the stale chip) needs a page to open: the e-mail page, else the unit's own page.
const canOpen = (row: SmartSendListItem): boolean =>
    !isOldShape(row) && (hasEmail(row) || hasWa(row)) && allPresentEditable(row);
// Delete addresses the e-mail id, else the WhatsApp id (7.2).
const canDelete = (row: SmartSendListItem): boolean =>
    !isOldShape(row) && (row.CampaignID != null || row.WACampaignID != null) && allPresentEditable(row);

const SmartSendManageTab = ({ classes }: ClassesType) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const Redirect = useRedirect();
    const { windowSize, rowsPerPage, isRTL } = useSelector((s: any) => s.core);
    const { smartSendList, smartSendListStatus } = useSelector((s: any) => s.smartSend);

    const restored = GetPageNyName(PAGE_NAME_SS);
    // Clamp a restored page-size that isn't in ROWS_OPTIONS (the shared rowsPerPage cookie may hold a
    // value other screens use) so MUI TablePagination never gets an out-of-range value — as DataSources does.
    const restoredSize = restored?.SearchData?.PageSize;
    const initialPageSize = ROWS_OPTIONS.indexOf(restoredSize) > -1 ? restoredSize
        : (ROWS_OPTIONS.indexOf(rowsPerPage) > -1 ? rowsPerPage : 6);
    const [searchData, setSearchData] = useState({
        PageIndex: restored?.PageNumber ?? 1,
        PageSize: initialPageSize,
        SearchTerm: restored?.SearchData?.SearchTerm ?? '',
        OutdatedOnly: restored?.SearchData?.OutdatedOnly ?? false
    });
    const [searchInput, setSearchInput] = useState(searchData.SearchTerm);
    const [loading, setLoading] = useState(false);
    // delete (manage tab) — confirm-dialog target + in-flight flag + feedback toast.
    const [deleteTarget, setDeleteTarget] = useState<SmartSendListItem | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [toast, setToast] = useState<{ open: boolean; ok: boolean; msg: string }>({ open: false, ok: true, msg: '' });

    const items: SmartSendListItem[] = smartSendList?.items ?? [];
    const total: number = smartSendList?.total ?? 0;

    // ── load on searchData change + persist page state (own key) ──
    useEffect(() => {
        loadList();
        SetPageState({
            PageName: PAGE_NAME_SS,
            PageNumber: searchData.PageIndex,
            SearchData: { SearchTerm: searchData.SearchTerm, PageSize: searchData.PageSize, OutdatedOnly: searchData.OutdatedOnly },
            SearchTerm: searchData.SearchTerm,
            IsDynamic: false
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchData]);

    const loadList = async () => {
        setLoading(true);
        const res: any = await dispatch(getSmartSendList({
            search: searchData.SearchTerm,
            outdatedOnly: searchData.OutdatedOnly,
            // 🔴 NO CHANNEL FILTER. `eSendChannel.EMAIL` was hard-coded here with the comment
            // "v1: EMAIL is the only wired channel", and that has not been true since 78a: a
            // WhatsApp-only mapping is a channel-3 row and this list was the ONLY screen that
            // could find it again. The grid was already built for this — it renders a Channel
            // column, keys its rows on (CampaignID, Channel) and passes the ROW's channel to
            // delete. Omitting the parameter is what DataSources_GetSmartSendList reads as
            // "every channel".
            pageSize: searchData.PageSize,
            rowsToSkip: (searchData.PageIndex - 1) * searchData.PageSize
        }));
        setLoading(false);
        // A restored page index can exceed the current page count (data shrank) → snap back to page 1
        // (mirrors DataSources.loadList).
        const data = res?.payload?.Data;
        if (data && (data.items?.length ?? 0) === 0 && (data.total ?? 0) > 0 && searchData.PageIndex > 1) {
            setSearchData(s => ({ ...s, PageIndex: 1 }));
        }
    };

    // ── handlers (mirror DataSources) ──
    const doSearch = () => setSearchData(s => ({ ...s, SearchTerm: searchInput.trim(), PageIndex: 1 }));
    const clearSearch = () => { setSearchInput(''); setSearchData(s => ({ ...s, SearchTerm: '', PageIndex: 1 })); };
    const changePage = (_: any, page: number) => setSearchData(s => ({ ...s, PageIndex: page + 1 }));
    const changeRows = (e: any) => setSearchData(s => ({ ...s, PageSize: parseInt(e.target.value, 10), PageIndex: 1 }));
    const toggleOutdated = (e: any) => setSearchData(s => ({ ...s, OutdatedOnly: e.target.checked, PageIndex: 1 }));
    // Distinguish "no data yet" from "no match": when a search term OR the outdatedOnly filter is
    // active, an empty grid means "no results" — and the reset must clear BOTH (the toolbar's
    // clearSearch clears only the term).
    const hasFilter = !!searchData.SearchTerm || searchData.OutdatedOnly;
    const resetFilters = () => { setSearchInput(''); setSearchData(s => ({ ...s, SearchTerm: '', OutdatedOnly: false, PageIndex: 1 })); };

    // The version chip is the ONLY entry into the mapping screen from this tab (§7.3): amber +
    // clickable when a newer source version exists, otherwise plain text. Same route as SmartSendPicker.
    // 🔴 THE ROW'S CHANNEL TRAVELS, LIKE IT ALREADY DOES FOR DELETE.
    // Until 78g this grid could only ever hold channel-1 rows: DataSources_GetSmartSendList
    // INNER JOINed dbo.Campaigns, and a channel-3 arc row has CampaignID NULL, so it was
    // dropped. 78g makes that a LEFT JOIN and projects ISNULL(CampaignID, WACampaignID) — so
    // from now on this id can be a WACampaignID, and the receiving screen defaults a missing
    // ?channel= to EMAIL (SmartSendScreen.tsx:99-103).
    // Without the parameter the best case is a 404 dead end on the ONLY screen that lists a
    // WhatsApp-only mapping; the worse case is that the sub-account happens to own an e-mail
    // campaign with the same number — the two IDENTITY sequences are independent — and the
    // operator edits THAT campaign's mapping under a WhatsApp row's heading.
    // The row already carries its channel and three other places here honour it (rowKey,
    // channelLabel, deleteMapping "the ROW's channel, not a constant"); this door did not.
    // 🔴 2026-09-23 · `?dataSourceId=` — AND THIS DOOR DOES **NOT** GO THROUGH THE PICKER.
    // Every other entry point now opens `${sitePrefix}SmartSend` so the source is settled once,
    // in one place. This one is deliberately different and must stay so: it EDITS a mapping that
    // already exists, so the source is not an open question — sending the operator to a picker to
    // re-choose the source of a row they just clicked would be asking a settled question.
    // What it was missing is the other half: the row carries `DataSourceID` (SmartSend.ts:171)
    // and this link dropped it, so the mapping screen saw no `?dataSourceId=`, could not know the
    // source was settled, and rendered the full source grid instead of the locked heading — the
    // exact symptom reported from the stage test, reached through this door as well.
    // 12-fix-wave 7.2 — the e-mail page whenever the send has an e-mail (with or without WhatsApp; the
    // page reads its WhatsApp from the DB, no `wa` parameter, D5); the unit's own page (`channel=3`)
    // only for a WhatsApp-only send.
    const goToMapping = (row: SmartSendListItem) => {
        if (!canOpen(row)) return;
        const qs = new URLSearchParams();
        qs.set('dataSourceId', String(row.DataSourceID));
        let id: number | null = null;
        if (hasEmail(row)) id = row.CampaignID;
        else if (hasWa(row)) { id = row.WACampaignID; qs.set('channel', '3'); }
        if (id == null) return;
        Redirect({
            url: `${sitePrefix}Campaigns/SmartSend/${id}?${qs.toString()}`,
            openNewTab: false,
        });
    };

    // Delete a mapping (offered only on Created rows; the server re-checks the not-sent gate).
    // deleteMapping calls the delete endpoint ONLY — it never arms/attaches anything. On success
    // the row is gone, so refetch the current page.
    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        // 🔴 77 §2 — the ROW's channel, not a constant. The grid is keyed (CampaignID, Channel)
        // and already renders one row per channel (see rowKey), so deleting by campaign alone
        // would remove whichever row the server happened to pick.
        // 12-fix-wave 7.2 — the whole send: by the e-mail id when it has one, else by the WhatsApp id.
        const ref = deleteTarget.CampaignID != null
            ? { campaignId: deleteTarget.CampaignID, channel: eSendChannel.EMAIL }
            : { campaignId: deleteTarget.WACampaignID as number, channel: eSendChannel.WHATSAPP };
        const res: any = await dispatch(deleteMapping(ref));
        const ok = res?.payload?.StatusCode === 200;
        setDeleting(false);
        setDeleteTarget(null);
        // 12-fix-wave 7.10 — a send that went out answers SEND_ALREADY_SENT. A unit that went out (or is
        // going out) answers a WhatsApp code (WA_ALREADY_SENT / WA_EDIT_BLOCKED_DURING_SEND, contract 5.8):
        // its existing `whatsapp.error` text. Any other refusal keeps today's text.
        const msg: string | null = res?.payload?.Message ?? null;
        const waKey = msg ? `DataSources.send.whatsapp.error.${msg}` : null;
        const failKey = msg === 'SEND_ALREADY_SENT'
            ? 'DataSources.send.errors.sendAlreadySent'
            : (waKey && msg && msg.indexOf('WA_') === 0 && i18n.exists(waKey) ? waKey : 'DataSources.send.manage.deleteFailed');
        setToast({ open: true, ok, msg: t(ok ? 'DataSources.send.manage.deleteOk' : failKey) });
        if (ok) loadList();
    };

    // ── cell renderers ──
    const rowStyle = { head: classes.tableRowHead, root: classes.tableRowRoot };
    const cellStyle = { head: classes.tableCellHead, body: classes.tableCellBody, root: classes.tableCellRoot };

    // 12-fix-wave 7.2 — `icon` marks which channel a chip belongs to on a "both" row.
    const renderStatusChip = (status: number | null, icon?: ReactElement) => {
        const key = status != null ? STATUS_KEY[status] : undefined;
        return key ? <Chip size="small" icon={icon} label={t(key)} /> : null;
    };

    // 12-fix-wave 7.2 — the status of EACH present channel.
    const renderStatusChips = (row: SmartSendListItem) => {
        const both = hasEmail(row) && hasWa(row);
        return (
            <Box style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
                {hasEmail(row) && renderStatusChip(row.CampaignStatus, both ? <Email fontSize="small" /> : undefined)}
                {hasWa(row) && renderStatusChip(row.WACampaignStatus, both ? <WhatsApp fontSize="small" /> : undefined)}
            </Box>
        );
    };

    // 12-fix-wave 7.2 — the name of EACH present channel (with its icon on a "both" row).
    const renderNames = (row: SmartSendListItem) => {
        const both = hasEmail(row) && hasWa(row);
        const nameLine = (name: string | null, icon: ReactElement | null) => (
            <Typography style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                {icon && <span style={{ display: 'inline-flex', verticalAlign: 'middle', marginInlineEnd: 4 }}>{icon}</span>}
                {wrapAtUnderscores(name)}
            </Typography>
        );
        return (
            <>
                {hasEmail(row) && nameLine(row.CampaignName, both ? <Email fontSize="small" /> : null)}
                {hasWa(row) && nameLine(row.WACampaignName, both ? <WhatsApp fontSize="small" /> : null)}
            </>
        );
    };

    // The stale chip only OPENS the send, like Edit (7.2) — and only while the send is still editable.
    const renderVersionChip = (row: SmartSendListItem) => {
        if (row.IsOutdated) {
            const open = canOpen(row);
            return (
                <Tooltip title={t('DataSources.send.stale.title')} PopperProps={{ style: { direction: isRTL ? 'rtl' : 'ltr' } }}>
                    <Chip
                        size="small"
                        clickable={open}
                        onClick={open ? () => goToMapping(row) : undefined}
                        label={t('DataSources.send.manage.staleChip', { mapped: row.MappedVersionNumber, latest: row.LatestVersionNumber })}
                        style={{ backgroundColor: 'var(--pls-d-surface-2, #FEF0C7)', color: 'var(--pls-d-warning, #B54708)', fontWeight: 700, direction: 'ltr' }}
                    />
                </Tooltip>
            );
        }
        return <Typography style={{ direction: 'ltr' }}>{`V${row.MappedVersionNumber}`}</Typography>;
    };

    // 🔴 The channel arrives from the server (DataSources_GetSmartSendList). Unknown resolves to
    // the raw number — never to another channel's label. The lookup used to end in
    // `?? CHANNELS[0]`, so an unrecognised channel was listed as "אימייל".
    // 12-fix-wave 7.2 / 7.10 — the label says which channels the send has; no single Channel value.
    const channelLabel = (row: SmartSendListItem) => {
        if (hasEmail(row) && hasWa(row)) return t('DataSources.send.manage.channelBoth');
        if (hasEmail(row)) return t('DataSources.send.manage.channelEmail');
        if (hasWa(row)) return t('DataSources.send.manage.channelWhatsapp');
        return '—';
    };

    const updatedText = (row: SmartSendListItem) =>
        row.LastUpdated ? moment(row.LastUpdated).format(DateFormats.DATE_TIME_24) : '—';

    // PK is (CampaignID, Channel) — key on both so a future SMS row for the same campaign is distinct.
    // 12-fix-wave 7.2 — one row per send: its two ids are its key.
    const rowKey = (row: SmartSendListItem) => `${row.CampaignID ?? 0}-${row.WACampaignID ?? 0}`;

    const renderActions = (row: SmartSendListItem, justify: 'center' | 'flex-start') => ((canOpen(row) || canDelete(row)) ? (
        <Box style={{ display: 'flex', gap: 6, justifyContent: justify, flexWrap: 'wrap' }}>
            {canOpen(row) && (
                <Button size="small" variant="outlined" color="primary" onClick={() => goToMapping(row)}>
                    {t('DataSources.send.manage.edit')}
                </Button>
            )}
            {canDelete(row) && (
                <Button size="small" variant="outlined" style={{ color: 'var(--pls-d-danger, #c0392b)', borderColor: '#e6b0aa' }} onClick={() => setDeleteTarget(row)}>
                    {t('DataSources.send.manage.delete')}
                </Button>
            )}
        </Box>
    ) : null);

    const renderRow = (row: SmartSendListItem) => (
        <TableRow key={rowKey(row)} classes={rowStyle}>
            <TableCell classes={cellStyle} align="center" className={clsx(classes.flex3)}>
                {renderNames(row)}
            </TableCell>
            <TableCell classes={cellStyle} align="center" className={clsx(classes.flex2)}>{renderStatusChips(row)}</TableCell>
            <TableCell classes={cellStyle} align="center" className={clsx(classes.flex2)}>
                {/* One element, not bare text: the cell is a flex container, so each <wbr> would become its own flex item. */}
                <span style={{ overflowWrap: 'anywhere' }}>{wrapAtUnderscores(row.DataSourceName)}</span>
            </TableCell>
            <TableCell classes={cellStyle} align="center" className={clsx(classes.flex2)}>{renderVersionChip(row)}</TableCell>
            <TableCell classes={cellStyle} align="center" className={clsx(classes.flex1)}>{channelLabel(row)}</TableCell>
            <TableCell classes={cellStyle} align="center" className={clsx(classes.flex2)} style={{ direction: 'ltr' }}>
                {updatedText(row)}
            </TableCell>
            <TableCell classes={cellStyle} align="center" className={clsx(classes.flex1, classes.noBorderOnLastCell)}>
                {/* Edit + Delete are offered ONLY on Created rows — once a send has started/finished
                    the mapping is locked (§ 'until sent'); the server re-checks both. */}
                {/* 12-fix-wave 7.2 — Edit + Delete only while EVERY present channel is editable (D1). */}
                {renderActions(row, 'center')}
            </TableCell>
        </TableRow>
    );

    const renderPhoneRow = (row: SmartSendListItem) => (
        <TableRow key={rowKey(row)} classes={rowStyle}>
            <TableCell classes={cellStyle} align="right" style={{ display: 'block' }}>
                <Card variant="outlined" style={{ marginBottom: 8 }}>
                    <CardContent>
                        {renderNames(row)}
                        <Typography style={{ fontSize: 12, color: 'var(--pls-d-text-2, #5b6b7b)', overflowWrap: 'anywhere' }}>{wrapAtUnderscores(row.DataSourceName)}</Typography>
                        <Box style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            {hasEmail(row) && renderStatusChip(row.CampaignStatus, hasWa(row) ? <Email fontSize="small" /> : undefined)}
                            {hasWa(row) && renderStatusChip(row.WACampaignStatus, hasEmail(row) ? <WhatsApp fontSize="small" /> : undefined)}
                            {renderVersionChip(row)}
                            <Chip size="small" variant="outlined" label={channelLabel(row)} />
                        </Box>
                        <Typography style={{ marginTop: 8, fontSize: 12, color: 'var(--pls-d-text-2, #5b6b7b)', direction: 'ltr' }}>{updatedText(row)}</Typography>
                        {(canOpen(row) || canDelete(row)) && (
                            <Box style={{ marginTop: 8 }}>{renderActions(row, 'flex-start')}</Box>
                        )}
                    </CardContent>
                </Card>
            </TableCell>
        </TableRow>
    );

    const renderTableHead = () => (
        <TableHead>
            <TableRow classes={rowStyle}>
                <TableCell classes={cellStyle} className={clsx(classes.flex3)} align="center">{t('DataSources.send.manage.columns.campaign')}</TableCell>
                <TableCell classes={cellStyle} className={clsx(classes.flex2)} align="center">{t('DataSources.send.manage.columns.status')}</TableCell>
                <TableCell classes={cellStyle} className={clsx(classes.flex2)} align="center">{t('DataSources.send.manage.columns.source')}</TableCell>
                <TableCell classes={cellStyle} className={clsx(classes.flex2)} align="center">{t('DataSources.send.manage.columns.version')}</TableCell>
                <TableCell classes={cellStyle} className={clsx(classes.flex1)} align="center">{t('DataSources.send.manage.columns.channel')}</TableCell>
                <TableCell classes={cellStyle} className={clsx(classes.flex2)} align="center">{t('DataSources.send.manage.columns.updated')}</TableCell>
                <TableCell classes={cellStyle} className={clsx(classes.flex1, classes.noBorderOnLastCell)} align="center">{t('DataSources.send.manage.columns.actions')}</TableCell>
            </TableRow>
        </TableHead>
    );

    const renderEmptyState = () => (
        <Box style={{ textAlign: 'center', padding: '48px 16px', color: 'var(--pls-d-text-2, #5b6b7b)' }}>
            <Typography style={{ fontSize: 18, fontWeight: 600 }}>
                {t(hasFilter ? 'DataSources.send.picker.noResults' : 'DataSources.send.manage.empty')}
            </Typography>
            {hasFilter && <Button onClick={resetFilters} style={{ marginTop: 12 }}>{t('DataSources.clearSearch')}</Button>}
        </Box>
    );

    const renderTable = () => {
        // `smartSendList` stays null until the first GetList resolves — avoids flashing the empty state on mount.
        if (smartSendList && !loading && items.length === 0) return renderEmptyState();
        return (
            <>
                <TableContainer className={classes.tableStyle}>
                    <Table className={classes.tableContainer}>
                        {windowSize !== 'xs' && renderTableHead()}
                        <TableBody>
                            {items.map(windowSize === 'xs' ? renderPhoneRow : renderRow)}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={total}
                    page={Math.max(0, searchData.PageIndex - 1)}
                    onPageChange={changePage}
                    rowsPerPage={searchData.PageSize}
                    onRowsPerPageChange={changeRows}
                    rowsPerPageOptions={ROWS_OPTIONS}
                    SelectProps={{ MenuProps: { PaperProps: { dir: isRTL ? 'rtl' : 'ltr' } } }}
                />
            </>
        );
    };

    return (
        <Box>
            <Box style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '12px 0', flexWrap: 'wrap' }}>
                <TextField
                    variant="outlined"
                    label={t('DataSources.send.picker.searchPlaceholder')}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') doSearch(); }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                    size="small"
                    style={{ minWidth: 320 }}
                />
                <Button onClick={doSearch} variant="outlined">{t('common.search')}</Button>
                {searchData.SearchTerm && <Button onClick={clearSearch}>{t('DataSources.clearSearch')}</Button>}
                <FormControlLabel
                    control={<Switch color="primary" checked={searchData.OutdatedOnly} onChange={toggleOutdated} />}
                    label={t('DataSources.send.manage.outdatedOnly')}
                />
            </Box>

            {renderTable()}

            <Loader isOpen={loading && smartSendListStatus === 'loading'} />

            {/* Delete confirm — destructive, so the primary button is red and there is no
                autofocus/ok-by-default. Closing is blocked while the delete is in flight. */}
            {/* dir is required and is NOT inherited: MUI v4 Dialogs portal into document.body,
                outside the <div dir={isRTL...}> at App.js:1018, and <html dir> stays "ltr" because
                App.js:727-730 writes it once at mount from i18n.language — still the 'en' default
                from i18n.js:20 at that point. Every sibling dialog in this folder carries it
                (ExportDialog.tsx:71, EditColumnDialog.tsx:87, UploadWizardDialog.tsx:555). */}
            <Dialog open={!!deleteTarget} onClose={() => { if (!deleting) setDeleteTarget(null); }} maxWidth="xs" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
                {/* Sizes are set inline rather than via components/dialogStyles.ts: this dialog builds its
                    chrome from plain Boxes, so that class's DialogTitle/Content/Actions selectors would not
                    match. Keep these numbers in step with dialogStyles.ts — it is the source of truth. */}
                <Box style={{ padding: '16px 24px', borderBottom: '1px solid var(--pls-d-border, #e0e0e0)' }}>
                    <Typography variant="h6" style={{ fontSize: 22, fontWeight: 700 }}>{t('DataSources.send.manage.deleteConfirmTitle')}</Typography>
                </Box>
                <Box style={{ padding: 24 }}>
                    <Typography variant="body2" color="textSecondary" style={{ fontSize: 17 }}>
                        {/* 12-fix-wave 7.2 — a "both" send gets the 7.10 text; one channel keeps today's text,
                            with that channel's own name. */}
                        {deleteTarget && hasEmail(deleteTarget) && hasWa(deleteTarget)
                            ? t('DataSources.send.manage.deleteConfirmBodyBoth', {
                                emailName: deleteTarget.CampaignName ?? '',
                                waName: deleteTarget.WACampaignName ?? '',
                            })
                            : t('DataSources.send.manage.deleteConfirmBody', {
                                name: (deleteTarget && hasEmail(deleteTarget)
                                    ? deleteTarget.CampaignName
                                    : deleteTarget?.WACampaignName) ?? '',
                            })}
                    </Typography>
                </Box>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 24px', borderTop: '1px solid var(--pls-d-border, #e0e0e0)' }}>
                    <Button variant="contained" disabled={deleting} onClick={confirmDelete} style={{ backgroundColor: '#c0392b', color: '#fff' }}>
                        {deleting ? <CircularProgress size={18} color="inherit" /> : t('DataSources.send.manage.delete')}
                    </Button>
                    <Box style={{ flex: 1 }} />
                    <Button disabled={deleting} onClick={() => setDeleteTarget(null)}>{t('DataSources.send.cancel')}</Button>
                </Box>
            </Dialog>

            <Snackbar
                open={toast.open}
                autoHideDuration={3500}
                onClose={() => setToast({ ...toast, open: false })}
                message={toast.msg}
                /* Snackbar is a portalled layer too, and it was the one this screen's RTL sweep missed.
                   It carries Hebrew success/failure text, so without a direction it renders as an LTR
                   paragraph (trailing punctuation and alignment flip). The anchor is mirrored for the
                   same reason the Send glyph is: "bottom-left" is the far corner in LTR and the near one
                   in RTL. A plain block comment, not a braced one — inside a JSX attribute list a braced
                   comment is not a legal attribute. */
                anchorOrigin={{ vertical: 'bottom', horizontal: isRTL ? 'right' : 'left' }}
                ContentProps={{ style: { backgroundColor: toast.ok ? '#2e7d32' : '#c0392b', direction: isRTL ? 'rtl' : 'ltr' } }}
            />
        </Box>
    );
};

export default SmartSendManageTab;
